import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { AlertTriangle, CheckCircle, MapPin, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader, LoadingState, StatusBadge, DataTable, type Column } from '@/components/common';
import { DetailLayout, DetailList, RailCard, StatStrip } from '@/components/common/DetailPanels';
import {
  goodsReceiptApi,
  type GoodsReceipt,
  type GoodsReceiptItem,
  type ReceiveItemDto,
} from '@/api/services/goods-receipt.api';
import { formatDateTime, formatNumber, toTitleCase } from '@/lib/utils';
import { getErrorMessage } from '@/api/client';

/**
 * One goods receipt — what arrived, what was accepted, and what was wrong with
 * it.
 *
 * This was a sheet on the Goods Receipt list, which put a document with a QC
 * outcome and a discrepancy trail behind a row click. Receiving is a hand-off
 * between the yard and whoever raised the PO, and "GR-2025-0500 is short two"
 * needs to be a link.
 *
 * The receive and discrepancy dialogs came with it. They act on the receipt, so
 * they belong where the receipt is rather than on the list behind it.
 */
export default function GoodsReceiptDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<GoodsReceipt | null>(null);
  const [loading, setLoading] = useState(true);

  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveItems, setReceiveItems] = useState<Record<string, ReceiveItemDto>>({});
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  const [discrepancyType, setDiscrepancyType] = useState('QUANTITY_MISMATCH');
  const [discrepancySeverity, setDiscrepancySeverity] = useState('MEDIUM');
  const [discrepancyDescription, setDiscrepancyDescription] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setReceipt(await goodsReceiptApi.getById(id));
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/goods-receipt');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const openReceive = () => {
    if (!receipt?.items) return;
    // Quantities start blank rather than pre-filled with what was expected:
    // receiving is a count, and a pre-filled figure invites confirming rather
    // than counting.
    const seeded: Record<string, ReceiveItemDto> = {};
    receipt.items.forEach((item) => {
      seeded[item.id] = { goodsReceiptItemId: item.id, receivedQty: 0, qualityStatus: 'PASSED' };
    });
    setReceiveItems(seeded);
    setReceiveOpen(true);
  };

  const submitReceive = async () => {
    if (!receipt) return;
    const items = Object.values(receiveItems).filter((i) => i.receivedQty > 0);
    if (items.length === 0) {
      toast.error('Enter a quantity for at least one item');
      return;
    }
    try {
      await goodsReceiptApi.receiveItems(receipt.id, items);
      toast.success('Items received');
      setReceiveOpen(false);
      setReceiveItems({});
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const submitDiscrepancy = async () => {
    if (!receipt || !discrepancyDescription) {
      toast.error('Describe the discrepancy');
      return;
    }
    try {
      await goodsReceiptApi.reportDiscrepancy(receipt.id, {
        type: discrepancyType as never,
        description: discrepancyDescription,
        severity: discrepancySeverity as never,
      });
      toast.success('Discrepancy reported');
      setDiscrepancyOpen(false);
      setDiscrepancyDescription('');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  if (loading) return <LoadingState variant="detail" />;
  if (!receipt) return null;

  const items = receipt.items ?? [];
  const totals = items.reduce(
    (acc, i) => ({
      expected: acc.expected + i.expectedQty,
      received: acc.received + i.receivedQty,
      accepted: acc.accepted + i.acceptedQty,
      rejected: acc.rejected + i.rejectedQty,
    }),
    { expected: 0, received: 0, accepted: 0, rejected: 0 }
  );
  const open = receipt.status !== 'COMPLETED' && receipt.status !== 'CANCELLED';

  const itemColumns: Column<GoodsReceiptItem>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (i) => (
        <div>
          <p className="font-medium">{i.purchaseOrderItem?.item.name ?? '—'}</p>
          <p className="text-xs text-muted-foreground">{i.purchaseOrderItem?.item.sku}</p>
        </div>
      ),
    },
    {
      key: 'expectedQty',
      header: 'Expected',
      sortable: true,
      className: 'text-right',
      render: (i) => <span className="block text-right tabular-nums">{i.expectedQty}</span>,
    },
    {
      key: 'receivedQty',
      header: 'Received',
      sortable: true,
      className: 'text-right',
      render: (i) => <span className="block text-right tabular-nums">{i.receivedQty}</span>,
    },
    {
      key: 'acceptedQty',
      header: 'Accepted',
      sortable: true,
      className: 'text-right',
      render: (i) => (
        <span className="block text-right tabular-nums">
          {i.acceptedQty}
          {i.rejectedQty > 0 && (
            <span className="ml-1 text-status-destructive-foreground">(-{i.rejectedQty})</span>
          )}
        </span>
      ),
    },
    {
      key: 'batchNumber',
      header: 'Batch',
      render: (i) => (
        <span className="text-muted-foreground">
          {i.batchNumber || <span className="text-muted-foreground/60">—</span>}
        </span>
      ),
    },
    {
      key: 'qualityStatus',
      header: 'Quality',
      sortable: true,
      render: (i) => <StatusBadge status={i.qualityStatus} />,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Goods Receipt', href: '/goods-receipt' },
            { label: receipt.receiptNumber },
          ]}
          title={receipt.receiptNumber}
          description={`Received ${formatDateTime(receipt.receivedAt)} at ${receipt.location.name}`}
          titleAdornment={<StatusBadge status={receipt.status} />}
          actions={
            open ? (
              <div className="flex gap-2">
                <Button onClick={openReceive}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Receive items
                </Button>
                <Button variant="outline" onClick={() => setDiscrepancyOpen(true)}>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Report issue
                </Button>
              </div>
            ) : undefined
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Delivery" icon={<Truck className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    {
                      label: 'Purchase order',
                      value: receipt.purchaseOrder ? (
                        <Link
                          to={`/purchase-orders/${receipt.purchaseOrder.id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {receipt.purchaseOrder.poNumber}
                        </Link>
                      ) : null,
                      when: !!receipt.purchaseOrder,
                    },
                    {
                      label: 'Supplier',
                      value: receipt.purchaseOrder?.supplier?.name,
                      when: !!receipt.purchaseOrder?.supplier?.name,
                    },
                    {
                      label: 'Shipment',
                      value: receipt.shipment ? (
                        <Link
                          to={`/shipments/${receipt.shipment.id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {receipt.shipment.shipmentNumber}
                        </Link>
                      ) : null,
                      when: !!receipt.shipment,
                    },
                    {
                      label: 'Carrier',
                      value: receipt.carrierName,
                      when: !!receipt.carrierName,
                    },
                    {
                      label: 'Delivery note',
                      value: receipt.deliveryNoteNumber,
                      when: !!receipt.deliveryNoteNumber,
                    },
                  ]}
                />
              </RailCard>

              <RailCard title="Receipt" icon={<MapPin className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    {
                      label: 'Location',
                      value: (
                        <Link
                          to={`/locations/${receipt.locationId}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {receipt.location.name}
                        </Link>
                      ),
                    },
                    { label: 'Received by', value: receipt.receivedBy.fullName },
                    { label: 'Received at', value: formatDateTime(receipt.receivedAt) },
                    { label: 'Lines', value: items.length },
                    {
                      label: 'Rejected',
                      value: formatNumber(totals.rejected),
                      // Rejected stock is the figure somebody has to act on, so
                      // it is the one that gets colour.
                      tone: totals.rejected > 0 ? 'destructive' : 'default',
                      when: totals.rejected > 0,
                    },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Items</h3>
                  <CardDescription>What was expected against what was accepted</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <StatStrip
                    stats={[
                      { label: 'Expected', value: formatNumber(totals.expected) },
                      { label: 'Received', value: formatNumber(totals.received) },
                      {
                        label: 'Accepted',
                        value: (
                          <span
                            className={
                              totals.rejected > 0 ? 'text-status-warning-foreground' : undefined
                            }
                          >
                            {formatNumber(totals.accepted)}
                          </span>
                        ),
                      },
                    ]}
                  />
                  <DataTable<GoodsReceiptItem>
                    pagination
                    data={items}
                    columns={itemColumns}
                    emptyTitle="No lines"
                    emptyDescription="This receipt has no item lines."
                  />
                </CardContent>
              </Card>

              {receipt.discrepancies && receipt.discrepancies.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Discrepancies</h3>
                    <CardDescription>
                      Issues raised against this receipt, and how they were settled
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {receipt.discrepancies.map((d) => (
                      <div key={d.id} className="rounded-lg border p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{toTitleCase(d.type)}</p>
                          <div className="flex gap-2">
                            <StatusBadge status={d.severity} />
                            <StatusBadge status={d.status} />
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{d.description}</p>
                        {d.resolution && (
                          <p className="mt-2 text-sm text-status-success-foreground">
                            {d.resolution}
                          </p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {receipt.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{receipt.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>

      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Receive items</DialogTitle>
            <DialogDescription>Enter the quantities counted for each line</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {items.map((item) => (
              <div key={item.id} className="space-y-3 rounded-lg border p-4">
                <div>
                  <p className="font-medium">{item.purchaseOrderItem?.item.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.purchaseOrderItem?.item.sku} · expected {item.expectedQty}
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Received</Label>
                    <Input
                      type="number"
                      min="0"
                      value={receiveItems[item.id]?.receivedQty || 0}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            receivedQty: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Rejected</Label>
                    <Input
                      type="number"
                      min="0"
                      value={receiveItems[item.id]?.rejectedQty || 0}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: {
                            ...prev[item.id],
                            rejectedQty: parseInt(e.target.value) || 0,
                          },
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Quality</Label>
                    <Select
                      value={receiveItems[item.id]?.qualityStatus || 'PASSED'}
                      onValueChange={(value) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], qualityStatus: value as never },
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PASSED">Passed</SelectItem>
                        <SelectItem value="FAILED">Failed</SelectItem>
                        <SelectItem value="CONDITIONAL">Conditional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Batch number</Label>
                    <Input
                      value={receiveItems[item.id]?.batchNumber || ''}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], batchNumber: e.target.value },
                        }))
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Expiry date</Label>
                    <Input
                      type="date"
                      value={receiveItems[item.id]?.expiryDate || ''}
                      onChange={(e) =>
                        setReceiveItems((prev) => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], expiryDate: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitReceive}>Confirm receipt</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={discrepancyOpen} onOpenChange={setDiscrepancyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report a discrepancy</DialogTitle>
            <DialogDescription>Raise an issue against {receipt.receiptNumber}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={discrepancyType} onValueChange={setDiscrepancyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="QUANTITY_MISMATCH">Quantity mismatch</SelectItem>
                  <SelectItem value="QUALITY_ISSUE">Quality issue</SelectItem>
                  <SelectItem value="DAMAGED_GOODS">Damaged goods</SelectItem>
                  <SelectItem value="WRONG_ITEM">Wrong item</SelectItem>
                  <SelectItem value="MISSING_DOCUMENTATION">Missing documentation</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Severity</Label>
              <Select value={discrepancySeverity} onValueChange={setDiscrepancySeverity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={discrepancyDescription}
                onChange={(e) => setDiscrepancyDescription(e.target.value)}
                placeholder="What is wrong with this receipt?"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscrepancyOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submitDiscrepancy}>Report issue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
